// etapas.js
//
// Fuente ÚNICA de las etiquetas de etapa (#1738). Antes había cuatro tablas copiadas
// (DataTab, la cabecera de la ficha, UserDrawer/DataSection y ProfileRequiredGate) y cada una
// dejaba fuera valores distintos. Ahora el alta, la celda ETAPA del admin, la ficha 360, el
// drawer y el gate leen de acá.
//
// PAR EN EL BACKEND: `backend/app/user_api/models.py` `ProfessionalProfile.Etapa` y
// `Profession`. Son dos repos: si cambia un valor allá, cambia acá. Cubre R1, R4, R11 y R12
// — .claude/specs/1738-etapa-alta/

// Las 6 que ofrece el alta, en este orden (R1).
export const ETAPAS_ALTA = [
  { value: "estudiante", label: "Estudiante" },
  { value: "interno", label: "Interno" },
  { value: "medico_general", label: "Médico general" },
  { value: "becado", label: "Becado" },
  { value: "especialista", label: "Especialista" },
  { value: "no_medico", label: "No soy médico" },
];

// Legados que el alta ya no ofrece pero hay cuentas guardadas con ellos: se muestran, no se
// migran (decisión de Sebastián, 18-oct-2026: "dejarlos como están").
export const ETAPAS_LEGADAS = [
  { value: "staff", label: "Staff / especialista" },
  { value: "otro", label: "Otro" },
];

export const ETAPA_LABELS = Object.fromEntries([...ETAPAS_ALTA, ...ETAPAS_LEGADAS].map((e) => [e.value, e.label]));

// R4: lo que responde quien no es médico. Los `value` son los de `Profession` del backend.
export const PROFESIONES_NO_MEDICO = [
  { value: "KINESIOLOGIST", label: "Kinesiólogo/a" },
  { value: "NURSE", label: "Enfermero/a" },
  { value: "TENS", label: "TENS" },
  { value: "OTHER", label: "Otra de la salud" },
  { value: "NO_SALUD", label: "No soy de la salud" },
];
const PROFESION_NO_MEDICO_LABEL = Object.fromEntries(PROFESIONES_NO_MEDICO.map((p) => [p.value, p.label]));

// R3 / R4 / R14: las preguntas hijas que abre cada etapa, en el orden en que se pintan.
//   estudiante, interno          → universidad (obligatoria, «Dónde estudias»)
//   medico_general               → universidad (opcional, «Dónde estudiaste»)
//   becado                       → especialidad + universidad (obligatorias, «Universidad de tu beca»)
//   especialista                 → especialidad (obligatoria) + universidad (opcional)
//   no_medico                    → profesion_no_medico
// PAR EN EL BACKEND: `OnboardingAnswersSerializer.validate` exige especialidad (becado y
// especialista), profesión (no médico) y universidad (estudiante, interno y becado) SIEMPRE que el
// PATCH trae `etapa` (R5, R15), también si la etapa es la que ya estaba guardada: la excepción
// «solo si cambia» se retiró en la v3.
export const hijasDeEtapa = (etapa) => {
  switch (etapa) {
    case "estudiante":
    case "interno":
    case "medico_general":
      return ["universidad"];
    case "becado":
    case "especialista":
      return ["especialidad", "universidad"];
    case "no_medico":
      return ["profesion_no_medico"];
    default:
      return [];
  }
};

// La universidad no se exige al médico general ni al especialista (R14).
export const universidadOpcionalEn = (etapa) => etapa === "medico_general" || etapa === "especialista";

// Texto de la pregunta de universidad según la etapa (mockup v3).
export const tituloUniversidad = (etapa) => {
  if (etapa === "becado") return "Universidad de tu beca";
  if (etapa === "medico_general" || etapa === "especialista") return "Dónde estudiaste";
  return "Dónde estudias";
};

// Etapas que se pueden guardar sin ninguna hija obligatoria (las que el gate de datos faltantes
// puede ofrecer solas, sin que el backend responda 400).
export const ETAPAS_SIN_HIJA = ETAPAS_ALTA.filter(
  (e) => !hijasDeEtapa(e.value).some((h) => !(h === "universidad" && universidadOpcionalEn(e.value))),
);

// R11: el texto de la celda ETAPA para una cuenta del admin. Manda la etapa (con la especialidad
// al lado si la tiene; o lo que eligió si no es médico); SOLO sin etapa cae a la profesión.
// "" si no hay ninguna de las dos (la celda pinta "sin etapa"). Nunca devuelve "—".
export const etiquetaEtapa = (user) => {
  const etapa = user?.etapa || "";
  if (etapa) {
    if (etapa === "no_medico" && PROFESION_NO_MEDICO_LABEL[user.profession]) {
      return PROFESION_NO_MEDICO_LABEL[user.profession];
    }
    const base = ETAPA_LABELS[etapa] || etapa;
    const esp = (user.specialty_text || "").trim();
    return (etapa === "becado" || etapa === "especialista") && esp ? `${base} · ${esp}` : base;
  }
  return user?.profession_display || PROFESION_NO_MEDICO_LABEL[user?.profession] || user?.profession || "";
};

// Tono de la píldora (el color lo resuelve `shared.js` con tokens): verde = en formación,
// ámbar = no médico/otro, azul = el resto.
export const tonoEtapa = (user) => {
  const etapa = user?.etapa || "";
  if (etapa === "interno" || etapa === "estudiante") return "ok";
  if (etapa === "no_medico" || etapa === "otro") return "warn";
  if (etapa) return "info";
  const p = user?.profession;
  if (p === "INTERNO" || p === "ESTUDIANTE") return "ok";
  if (p && p !== "DOCTOR") return "warn";
  return "info";
};
